import Head from 'next/head';
import {useRouter} from 'next/router';

import {DEFAULT_LOCALE, SUPPORTED_LOCALES, toOgLocale} from 'GlobalShared/locales';

const SITE_NAME = 'little walks';
const SITE_NAME_SUFFIX = ` | ${SITE_NAME}`;
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

/**
 * Builds the display title with site name suffix. Skips appending the suffix
 * if the title already ends with it (e.g. Payload's `generateSeoTitle` hook
 * already appends it to `meta.title`), to avoid a doubled " | little walks".
 *
 * @param title Raw page title.
 * @returns Formatted title string.
 */
const resolveTitle = (title?: string | null) => {
    if (!title) return SITE_NAME;

    return title.endsWith(SITE_NAME_SUFFIX) ? title : `${title}${SITE_NAME_SUFFIX}`;
};

/**
 * Resolves the router locale to one of the supported locales, falling back to the default.
 *
 * @param locale The raw `next/router` locale value.
 * @returns The resolved, supported locale.
 */
const resolveLocale = (locale?: string) => (locale ?? DEFAULT_LOCALE) as 'de';

/**
 * The props for the {@link SeoHead} component.
 */
interface ComponentProps {
    /** Meta description for search engines and social previews. */
    description?: string | null;
    /** GeoCoordinates [lat, lng] for geo meta tags (local SEO). */
    geo?: {lat: number; lng: number; placename?: string; region?: string} | null;
    /** Absolute URL to the primary image for og:image. */
    image?: string | null;
    /** JSON-LD structured data objects to inject as script tags. */
    jsonLd?: Record<string, unknown>[];
    /** Override og:type (defaults to "website", use "product" for walk pages). */
    ogType?: string;
    /** Page title. Falls back to the site name when omitted. */
    title?: string | null;
    /** Canonical URL of the page. */
    url?: string | null;
}

/**
 * Renders SEO-relevant `<head>` tags (title, meta, OG, hreflang, geo, JSON-LD).
 *
 * @param props             The component props.
 * @param props.description Meta description text.
 * @param props.geo         Geo coordinates for local SEO meta tags.
 * @param props.image       Absolute image URL for og:image.
 * @param props.jsonLd      Array of JSON-LD objects for structured data.
 * @param props.ogType      Open Graph type override.
 * @param props.title       Page title.
 * @param props.url         Canonical URL.
 * @returns A `next/head` fragment with SEO meta tags.
 */
const SeoHead = ({description, geo, image, jsonLd, ogType = 'website', title, url}: ComponentProps) => {
    const {asPath, locale} = useRouter();

    return (
        <Head>
            <title>{resolveTitle(title)}</title>
            {description && <meta content={description} name="description" />}
            {/* Open Graph */}
            <meta content={resolveTitle(title)} property="og:title" />
            {description && <meta content={description} property="og:description" />}
            <meta content={SITE_NAME} property="og:site_name" />
            <meta content={ogType} property="og:type" />
            <meta content={toOgLocale(resolveLocale(locale))} property="og:locale" />
            {SUPPORTED_LOCALES.filter(l => l !== resolveLocale(locale)).map(l => (
                <meta key={l} content={toOgLocale(l)} property="og:locale:alternate" />
            ))}
            {url && <meta content={url} property="og:url" />}
            {image && <meta content={image} property="og:image" />}
            {/* Canonical + hreflang */}
            {url && <link href={url} rel="canonical" />}
            {SUPPORTED_LOCALES.map(l => (
                <link
                    key={l}
                    href={`${BASE_URL}${l === 'de' ? '' : `/${l}`}${asPath}`}
                    hrefLang={l}
                    rel="alternate"
                />
            ))}
            <link
                href={`${BASE_URL}${asPath}`}
                hrefLang="x-default"
                rel="alternate"
            />
            {/* Geo meta tags for local SEO */}
            {geo && (
                <>
                    <meta content={`${geo.lat};${geo.lng}`} name="geo.position" />
                    <meta content={`${geo.lat}, ${geo.lng}`} name="ICBM" />
                    {geo.placename && <meta content={geo.placename} name="geo.placename" />}
                    {geo.region && <meta content={geo.region} name="geo.region" />}
                </>
            )}
            {/* JSON-LD structured data */}
            {jsonLd && jsonLd.length > 0 && (
                <script
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(
                            jsonLd.length === 1 && '@graph' in jsonLd[0]
                                ? jsonLd[0]
                                : {
                                    '@context': 'https://schema.org',
                                    '@graph': jsonLd
                                }
                        )
                    }}
                    type="application/ld+json"
                />
            )}
        </Head>
    );
};

SeoHead.displayName = 'SeoHead';

export {SeoHead};